import type { BrandSlug } from '../shared/brand-slug';
import type { EAN } from '../shared/ean';
import type { ProductScorecard, SnapshotWeek, VendorBrandScorecard } from './scorecard';

/**
 * Reads the content scorecard: how completely each vendor describes a brand's products,
 * week by week, and what every vendor holds for a single product.
 *
 * The table is backend-populated and read-only from here. It keys its products in the
 * stored (masked) form on both of its row types, and this port is where that form stops:
 * callers ask with real codes and get real codes back (.claude/rules/portal-domain.md).
 */
export abstract class ScorecardRepository {
  /**
   * The newest weeks the current snapshot holds for one brand, newest first.
   *
   * The read behind it is capped by ROWS, and the table carries one row per vendor per week, so
   * the list stops well short of the full history -- and the surface's "older" step stops with
   * it.
   */
  abstract weeksOf(brand: BrandSlug): Promise<readonly SnapshotWeek[]>;

  /**
   * Every vendor's brand-level row for one brand. A week narrows the read to that snapshot
   * week; null asks across weeks, and that arm is unpaged: PostgREST caps it at `max_rows`, so
   * a brand whose history runs longer than that answers with as many rows as the cap allows
   * and no indication that it stopped early.
   */
  abstract vendorScorecardsOf(
    brand: BrandSlug,
    week: SnapshotWeek | null
  ): Promise<readonly VendorBrandScorecard[]>;

  /**
   * One product's row, or null when the current snapshot holds none for it. A week narrows
   * the read to that snapshot week; null takes the newest week the snapshot holds for it.
   */
  abstract productScorecardOf(
    ean: EAN,
    week: SnapshotWeek | null
  ): Promise<ProductScorecard | null>;
}
